import { CommonModule } from '@angular/common';
import { Component, computed, EventEmitter, Input, OnInit, Output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AudienceResponse } from '@libs/audience/config';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { MediaFile } from '../../../services/media-library.service';
import { RepositoryService } from '../../../services/repository.service';

import { TranslateModule } from '@ngx-translate/core';
import { AnalyticsEventsService } from '../../../services/analytics-events.service';
import { formatFileSizeCompact } from '../../utils/file-size.utils';
import { FileExtensions, isAudioFile, isDocFile, isImageFile, isPdfFile, isVideoFile, MediaAllowed, normalizeFileExtensions } from '../../utils/file-type.utils';
import { RepositoryShowFileDialogComponent } from '../repository-show-file-dialog/repository-show-file-dialog.component';

export enum FileType {
  Image = 'image',
  Video = 'video',
  Audio = 'audio',
  Doc = 'doc',
  Pdf = 'pdf'
}

@Component({
  selector: 'app-file-card',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    CheckboxModule,
    TagModule,
    TooltipModule,
    RepositoryShowFileDialogComponent,
    TranslateModule
  ],
  templateUrl: './file-card.component.html',
  styleUrls: ['./file-card.component.scss']
})
export class FileCardComponent implements OnInit {

  @Input({ required: true }) file!: MediaFile;
  @Input() selected = false;
  @Input() isSelectMode = false;
  @Input({ transform: normalizeFileExtensions })
  allowedTypes: FileExtensions[] = MediaAllowed.ALL;
  @Input() canSelect = true;
  @Input() canDownload = false;

  @Output() selectionChange = new EventEmitter<boolean>();
  @Output() download = new EventEmitter<MediaFile>();
  @Output() delete = new EventEmitter<MediaFile>();
  @Output() editFile = new EventEmitter<any>()

  @Input() audiences: AudienceResponse[] = [];
  @Input() parentAudiences: string[] = [];

  isImage: boolean = false;
  isVideo: boolean = false;
  isAudio: boolean = false;
  isDoc: boolean = false;
  isPdf: boolean = false;

  settingsActiveFile = signal<any | null>(null);
  showSettingsDialog = signal(false);
  showDetailFileDialog = signal(false);
  detailFileType = signal<FileType | undefined>(undefined);
  fileSizeLabel = computed(() => formatFileSizeCompact(this.file.size));

  settingsDialogConfig = computed(() => ({
    visible: this.showSettingsDialog(),
    item: this.settingsActiveFile(),
    message: 'Modifica impostazioni file'
  }));

  constructor(private readonly repositoryService: RepositoryService,
    private readonly analyticsEventsService: AnalyticsEventsService,) { }

  ngOnInit(): void {
    this.isImage = isImageFile(this.file.name);
    this.isVideo = isVideoFile(this.file.name);
    this.isAudio = isAudioFile(this.file.name);
    this.isDoc = isDocFile(this.file.name);
    this.isPdf = isPdfFile(this.file.name);
  }

  onDownload(): void {
    this.download.emit(this.file);
  }
  onDetail(): void {
    this.analyticsEventsService.trackEvents([{
      type: 'REPOSITORY_DOCUMENT_VIEW',
      config: {
        itemId: this.file.id,
      }
    }]);

    if (this.isImage) this.detailFileType.set(FileType.Image);
    else if (this.isVideo) this.detailFileType.set(FileType.Video);
    else if (this.isAudio) this.detailFileType.set(FileType.Audio);
    else if (this.isDoc) this.detailFileType.set(FileType.Doc);
    else if (this.isPdf) this.detailFileType.set(FileType.Pdf);
    else this.detailFileType.set(undefined);
    this.showDetailFileDialog.set(true);
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  onEdit() {
    const activeFile = { ...this.file, file: { name: this.file.name } }
    this.showSettingsDialog.set(true);
    this.settingsActiveFile.set(activeFile);
  }

  async confirmSettigs() {
    const file = this.settingsActiveFile()
    if (file) {
      this.editFile.emit(file);
      this.showSettingsDialog.set(false);
      this.settingsActiveFile.set(null);
    }
  }

  async cancelSettings() {
    const file = this.settingsActiveFile()

    if (file) {
      this.showSettingsDialog.set(false);
      this.settingsActiveFile.set(null);
    }
  }

  changeDownloadable(downloadable: boolean) {
    const activeFile = this.settingsActiveFile();
    if (activeFile)
      activeFile.downloadable = downloadable;
    this.settingsActiveFile.set(activeFile)
  }

  changeAudiances(audiances: string[]) {
    const activeFile = this.settingsActiveFile();
    if (activeFile)
      activeFile.audiences = audiances;
    this.settingsActiveFile.set(activeFile)
  }

  getFileType(): string {
    if (this.isImage) return FileType.Image;
    if (this.isVideo) return FileType.Video;
    if (this.isAudio) return FileType.Audio;
    if (this.isDoc) return FileType.Doc;
    return 'File';
  }

  onCloseShowDialog(): void {
    this.showDetailFileDialog.set(false);
    this.detailFileType.set(undefined);
  }

  onCardActivate(event: Event): void {
    if (this.isSelectMode) return;
    const target = event.target as HTMLElement;
    if (target.closest('p-button, button')) return;
    this.onDetail();
  }
}
